import React from "react";
import { CCButton, CCModal } from "@pymblecitizencoders/cc-ui";
/* lucide, not Font Awesome solid: it is the icon family cc-ui itself ships
   (CCIcons is lucide), and it is drawn as strokes rather than filled shapes, so
   the icons read as outlines and match the kit. cc-ui re-exports only 29 of
   them and has no distinct student/staff/parent glyph, which is why these come
   from the package directly rather than through CCIcons. */
import { CalendarCheck, MessagesSquare, SquarePen, LucideIcon } from "lucide-react";
import { EventModalHead } from "./EventModalHead";
import { EventAudienceChips } from "./EventAudienceChips";
import { AudienceKey } from "../../hooks/useInviteePicker";
import "./eventManager.css";

export type CreateMode = "find-time" | "assistant" | "manual";

interface EventCreateStartProps {
	open: boolean;
	mode: CreateMode;
	audiences: Set<AudienceKey>;
	onMode: (mode: CreateMode) => void;
	onToggleAudience: (key: AudienceKey) => void;
	onClose: () => void;
	onContinue: () => void;
	onSkipToManual: () => void;
}

const MODES: {
	key: CreateMode;
	icon: LucideIcon;
	title: string;
	blurb: string;
}[] = [
	{
		key: "find-time",
		icon: CalendarCheck,
		title: "Find a suitable time",
		blurb: "Choose groups or people and we'll suggest the best dates and times for everyone.",
	},
	{
		key: "assistant",
		icon: MessagesSquare,
		title: "Chat with your AI assistant",
		blurb: "Chat with AI to help you decide who to invite and find the perfect time.",
	},
	{
		key: "manual",
		icon: SquarePen,
		title: "Create manually",
		blurb: "I know the time already and want to create the event myself.",
	},
];

/**
 * First screen of the create flow: how do you want to start, and roughly who is
 * coming.
 *
 * The audience chips are a filter on the picker in the next screen, not a
 * commitment — ticking "Students" narrows what the search returns rather than
 * inviting anybody. The caption says as much, because a row of chips under
 * "Who is going?" reads like a selection if nothing tells you otherwise.
 *
 * All three modes are selectable. The assistant needs the availability
 * endpoints, which do not exist yet, but it is not disabled here: a greyed card
 * with no explanation is worse than one that leads somewhere honest, and the
 * next screen's assistant panel says plainly that it is not connected. See
 * `useEventScheduling` and the design doc it points at.
 */
export const EventCreateStart: React.FC<EventCreateStartProps> = ({
	open,
	mode,
	audiences,
	onMode,
	onToggleAudience,
	onClose,
	onContinue,
	onSkipToManual,
}) => {
	/**
	 * Continue leads to the picker, and the picker searches by audience — with
	 * no chip on there is nothing for it to search. Gated here rather than
	 * letting someone arrive at a step that cannot do anything.
	 *
	 * Cancel and "Skip and create manually" stay live: neither goes near the
	 * picker, so neither needs a scope.
	 */
	const hasAudience = audiences.size > 0;

	return (
	<CCModal
		open={open}
		onClose={onClose}
		title="Create a new event"
		className="em-start-modal"
		footer={
			/* Cancel sits apart on the left; the two ways forward group on the
			   right. The leaving action and the proceeding actions are different
			   kinds of thing and should not read as one row of equals. */
			<div className="em-modal-footer">
				<CCButton variant="outline" onClick={onClose}>
					Cancel
				</CCButton>
				<span className="em-modal-footer__right">
					<CCButton variant="outline" onClick={onSkipToManual}>
						Skip and create manually
					</CCButton>
					<CCButton
						variant="solid"
						colour="primary"
						disabled={!hasAudience}
						title={hasAudience ? undefined : "Choose who is going first"}
						onClick={onContinue}
					>
						Continue
					</CCButton>
				</span>
			</div>
		}
	>
		<div className="em-start">
			<EventModalHead
				title="Create a new event"
				icon={CalendarCheck}
				status={{ tone: "warning", text: "Pending time selection" }}
			/>

			<h3 className="em-start__heading">How would you like to get started?</h3>
			<p className="em-muted">
				We'll help you find the best time and connect the right people.
			</p>

			{/* A radiogroup, not three buttons: these are one choice with three
			    answers, and arrow keys should move between them. */}
			<div className="em-start__modes" role="radiogroup" aria-label="How to start">
				{MODES.map((m) => (
					<button
						key={m.key}
						type="button"
						role="radio"
						aria-checked={mode === m.key}
						className={`em-start__mode${mode === m.key ? " is-chosen" : ""}`}
						onClick={() => onMode(m.key)}
					>
						<span className="em-start__mode-icon" aria-hidden="true">
							<m.icon size={30} />
						</span>
						<span className="em-start__mode-title">{m.title}</span>
						<span className="em-muted">{m.blurb}</span>
						<span className="em-start__radio" aria-hidden="true" />
					</button>
				))}
			</div>

			<h3 className="em-start__heading">Who is going?</h3>
			<p className="em-muted">
				This filters the search on the next step. You'll pick the actual
				people there.
			</p>
			{/* Says why Continue is dead, rather than leaving someone to guess
			    at a greyed-out button. */}
			{!hasAudience && (
				<p className="em-muted">
					Choose at least one to continue, or skip and create the event
					manually.
				</p>
			)}

			<EventAudienceChips
				audiences={audiences}
				onToggle={onToggleAudience}
			/>
		</div>
	</CCModal>
	);
};
